import { useCallback, useContext } from 'react'

import { faArrowRightFromBracket as LogoutIcon } from '@fortawesome/pro-light-svg-icons/faArrowRightFromBracket'
import { faCircleQuestion as HelpIcon } from '@fortawesome/pro-light-svg-icons/faCircleQuestion'

import { faFolder as FolderIcon } from '@fortawesome/pro-light-svg-icons/faFolder'

import { faMoon } from '@fortawesome/pro-light-svg-icons/faMoon'
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser'
import { OptimizelyContext, useDecision } from '@optimizely/react-sdk'
import { loginEventDispatch } from '@space-lib-events/login'
import ContentLoader from '@space-lib/components/contentLoader'
import CustomButton from '@space-lib/components/customButton'
import { VariationTypes } from '@space-lib/components/customButton/types'
import DropdownPublic from '@space-lib/components/dropdown/user'

import Switch from '@space-lib/components/switch/user'

import { Actions, Entities } from '@space-lib/datahub'

import type { ActionEvents } from '@space-lib/datahub'
import { useMediaQuery } from '@space-lib/hooks'
import { ScreenSizes } from '@space-lib/styles'
import { LanguageUtils } from '@space-lib/utils/language'

import { useLogout } from '@space-shell/common/components/baseLayout/hooks/useLogout'
import CustomLanguage from '@space-shell/common/components/customLanguage'
import { AVAILABLE_LANGUAGES } from '@space-shell/common/components/customLanguage/constants'
import type { ApplicationLanguageType } from '@space-shell/common/components/customLanguage/constants'

import DropdownPrivate from '@space-shell/common/components/dropdown'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import useFeatureToggle from '@space-shell/common/hooks/useFeatureToggle'
import useGamificationToggle from '@space-shell/common/hooks/useGamificationToggle'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'

import { Toggles } from '@space-shell/common/services/toggle'
import { UserInfoRole } from '@space-shell/common/services/user'
import { CookieUtils } from '@space-shell/common/utils/cookie'
import { CookieKeys } from '@space-shell/common/utils/cookie/constants'
import { PURCHASES_MENU, getHelpMenuLink } from '@space-shell/constant/baseLayout'
import {
  RoleType,
  useMembershipStore,
  useUserInfoStore,
  useViewAsStore
} from '@space-shell/core/store'
import { useAuthStore } from '@space-shell/core/store'
import { lazyRemote } from '@space-shell/lib/runtime'
import GlobalSearch from '@space-shell/modules/globalSearch'
import { MembershipChatConsumer } from '@space-shell/modules/membershipChatConsumer'
import NotificationCenter from '@space-shell/modules/notificationCenter'
import Notifications from '@space-shell/modules/notifications'

import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, useParams } from 'react-router-dom'

const RankingCard = lazyRemote('gamification/RankingCard')

const MainHeaderBar = () => {
  const { logout } = useLogout()

  const {
    t,
    i18n: { language }
  } = useTranslation()
  const { role: viewAsRole, isEnabled: isViewAsEnabled } = useViewAsStore()
  const { role: userRole } = useUserInfoStore()
  const { theme, switchTheme } = useThemeContext()
  const { sendDataHubEvent } = useDataHubEvents()
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
  const { id: userId, userName } = useAuthStore()
  const { authStatus } = useAuthContext()
  const { membershipData } = useMembershipStore()
  const { optimizely } = useContext(OptimizelyContext)
  const navigate = useNavigate()
  const location = useLocation()
  const { lang } = useParams()
  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED
  const [enableMembershipChatFF] = useDecision('enable_membership_chat')

  const { data: isGamificationEnabled } = useGamificationToggle()

  const { data: isAccountConsumerEnabled } = useFeatureToggle({
    toggleName: Toggles.AccountConsumer,
    defaultValue: false,
    unleashId: userId
  })

  const { data: isNotificationsEnabled } = useFeatureToggle({
    toggleName: Toggles.EnableNotificationConsumer
  })
  const [shouldRenderNewNotificationCenter] = useDecision(
    'com_eng_web_enable_new_notification_center'
  )

  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.lg})`)

  const applicationLanguage = LanguageUtils.formatLanguage(
    language
  ).toLowerCase() as ApplicationLanguageType

  const [enableGlobalSearchForProducts] = useDecision('lex_enable_global_search_for_products')
  const isGlobalSearchVisible = enableGlobalSearchForProducts.enabled

  const handleClickInHelpMenu = useCallback(() => {
    sendDataHubEvent({
      group: Entities.HEADER_USER_MENU,
      name: Actions.CLICK_MENU_HELP
    })

    window.open(getHelpMenuLink(language), '_blank')
  }, [language, sendDataHubEvent])

  const handleClickInMyPurchasesMenu = useCallback(() => {
    sendDataHubEvent({
      group: Entities.HEADER_USER_MENU,
      name: Actions.CLICK_MENU_MY_PURCHASES
    })

    window.open(PURCHASES_MENU, '_blank')
  }, [sendDataHubEvent])

  const handleRedirectToSignUp = (eventNameOptimizely: string) => {
    sendDataHubEvent({
      group: Entities.HEADER_USER_MENU,
      name: Actions.CLICK_SIGNUP
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER_USER_MENU,
      name: Actions.CLICK_SIGNUP
    })

    if (eventNameOptimizely) {
      optimizely?.track(eventNameOptimizely)
    }

    window.open(`${process.env.SIGNUP_URL}${window.location.href}&systemOrigin=app-space`, '_self')
  }

  const handleRedirectToSignIn = (eventNameOptimizely: string) => {
    sendDataHubEvent({
      group: Entities.HEADER_USER_MENU,
      name: Actions.CLICK_SIGNIN
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER_USER_MENU,
      name: Actions.CLICK_SIGNIN
    })

    if (eventNameOptimizely) {
      optimizely?.track(eventNameOptimizely)
      optimizely?.track('click_login_primary')
    }

    loginEventDispatch({ signInRedirect: true })
  }

  const handleRedirectToMyAccount = () => {
    sendDataHubEvent({
      group: Entities.HEADER_USER_MENU,
      name: Actions.CLICK_MY_ACCOUNT
    })
    window.open(`${process.env.MY_ACCOUNT_URL}?systemOrigin=club-new`, '_blank')
  }

  const publicDropdownMenuItems = [
    {
      label: t('general.register.link'),
      handleClick: () => handleRedirectToSignUp('click_singup'),
      variation: VariationTypes.SECONDARY
    },
    {
      label: t('general.signin'),
      handleClick: () => handleRedirectToSignIn('click_login'),
      variation: VariationTypes.PRIMARY
    }
  ]

  const privateDropdownMenuItems = [
    {
      label: t('general.my_purchases'),
      handleClick: handleClickInMyPurchasesMenu,
      iconCustom: FolderIcon
    },
    {
      label: t('general.help'),
      handleClick: handleClickInHelpMenu,
      iconCustom: HelpIcon
    },
    {
      label: t('general.signout'),
      handleClick: logout,
      iconCustom: LogoutIcon
    }
  ]

  if (isAccountConsumerEnabled) {
    privateDropdownMenuItems.unshift({
      label: t('general.my_account'),
      handleClick: handleRedirectToMyAccount,
      iconCustom: faUser
    })
  }

  const handleClickLanguageOption = (languageValue: ApplicationLanguageType) => {
    if (AVAILABLE_LANGUAGES[languageValue]?.eventName) {
      sendDataHubEvent({
        group: Entities.HEADER_LANGUAGE_OPTIONS,
        name: AVAILABLE_LANGUAGES[languageValue]?.eventName as ActionEvents
      })
    }

    CookieUtils.setCookie(CookieKeys.COOKIE_LANGUAGE, languageValue)

    const newPath = location.pathname.replace(`/${lang}`, `/${languageValue}`)
    navigate(newPath + location.search)
    window.location.reload()
  }

  const renderOptions = () => {
    return (
      <>
        {isGamificationEnabled && isAuthenticated && <RankingCard />}
        {membershipData?.settings?.allowChangeTheme && (
          <Switch
            handleClick={switchTheme}
            label={t('general.theme.dark')}
            theme={theme}
            icon={faMoon}
          />
        )}

        <CustomLanguage
          aplicationLanguage={applicationLanguage}
          handleClickLanguageOption={handleClickLanguageOption}
          labelCustomLanguage={t('general.language')}
        />
      </>
    )
  }

  if (authStatus === AuthStatus.AUTHENTICATING) {
    return (
      <ContentLoader
        slot='actions-start'
        className='_w-5 _h-5 _overflow-hidden _mr-5 _rounded-circle'
      />
    )
  }

  const isOwner = userRole === UserInfoRole.OWNER

  const shouldShowUnauthenticatedHeader =
    !isAuthenticated ||
    (isAuthenticated && isOwner && isViewAsEnabled && viewAsRole === RoleType.UserLogout)

  const renderAccess = () => {
    if (!isAuthenticated && isMobile) {
      return (
        <CustomButton
          size='sm'
          onClick={() => handleRedirectToSignIn('click_login')}
          data-test='button-start'>
          {t('general.signin')}
        </CustomButton>
      )
    }

    return (
      <DropdownPublic
        icon={faUser}
        theme={theme}
        isLogged={isAuthenticated}
        dropdownItems={publicDropdownMenuItems}
        sendDataHubEvent={sendDataHubEvent}>
        {renderOptions()}
      </DropdownPublic>
    )
  }

  if (shouldShowUnauthenticatedHeader)
    return (
      <div slot='actions-end' className='_d-flex _w-full _justify-content-end'>
        <div className='_d-flex _align-items-center'>
          {!isMobile && (
            <>
              <CustomButton
                size='sm'
                variation={VariationTypes.SECONDARY}
                className='_mr-2'
                onClick={() => handleRedirectToSignUp('')}
                data-test='button-end'>
                {t('general.register.link')}
              </CustomButton>
              <CustomButton
                size='sm'
                onClick={() => handleRedirectToSignIn('')}
                data-test='button-start'>
                {t('general.signin')}
              </CustomButton>
            </>
          )}

          <div className='_ml-4'>{renderAccess()}</div>
        </div>
      </div>
    )

  return (
    <>
      {isGlobalSearchVisible && <GlobalSearch />}
      {enableMembershipChatFF?.enabled && <MembershipChatConsumer slot='actions-end' />}
      {isNotificationsEnabled &&
        (shouldRenderNewNotificationCenter.enabled ? <NotificationCenter /> : <Notifications />)}
      <DropdownPrivate
        userName={userName}
        dropdownItems={privateDropdownMenuItems}
        theme={theme}
        isLogged={isAuthenticated}>
        {renderOptions()}
      </DropdownPrivate>
    </>
  )
}

export default MainHeaderBar
